import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Trash2, CreditCard, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';

export const Expenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [formData, setFormData] = useState({
    type: '', amount: '', currency: 'EUR', description: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [expRes, typesRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/expenses`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/expense-types`, { withCredentials: true })
      ]);
      setExpenses(expRes.data);
      setExpenseTypes(typesRes.data);
    } catch (error) {
      console.error('Error fetching expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/expenses`,
        { ...formData, amount: parseFloat(formData.amount) },
        { withCredentials: true }
      );
      toast.success('Dépense ajoutée');
      setDialogOpen(false);
      setFormData({ type: '', amount: '', currency: 'EUR', description: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'ajout");
    }
  };

  const handleAddType = async () => {
    if (!newTypeName.trim()) return;
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/expense-types`,
        { name: newTypeName.trim() },
        { withCredentials: true }
      );
      toast.success('Type ajouté');
      setNewTypeName('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDeleteType = async (typeId) => {
    if (!window.confirm('Supprimer ce type ?')) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/expense-types/${typeId}`,
        { withCredentials: true }
      );
      toast.success('Type supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDeleteExpense = async (expId) => {
    if (!window.confirm('Supprimer cette dépense ?')) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/expenses/${expId}`,
        { withCredentials: true }
      );
      toast.success('Dépense supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const totalEur = expenses.filter(e => e.currency === 'EUR').reduce((s, e) => s + e.amount, 0);
  const totalVnd = expenses.filter(e => e.currency === 'VND').reduce((s, e) => s + e.amount, 0);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">Dépenses</h1>
            <p className="text-sm text-slate-600 mt-1">Dépenses mensuelles fixes. Le total s'ajoute aux frais de chaque mois.</p>
          </div>
          <div className="flex gap-3">
            <Button data-testid="manage-types-button" variant="outline" onClick={() => setTypeDialogOpen(true)}>
              <Settings size={16} className="mr-2" />
              Gérer les types
            </Button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="create-expense-button" className="bg-slate-900 hover:bg-slate-800">
                  <Plus size={18} className="mr-2" />
                  Nouvelle dépense
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Nouvelle dépense mensuelle</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Type*</Label>
                    <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                      <SelectTrigger data-testid="expense-type-select"><SelectValue placeholder="Choisir un type" /></SelectTrigger>
                      <SelectContent>
                        {expenseTypes.map(t => (
                          <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Montant*</Label>
                      <Input data-testid="expense-amount-input" type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} required />
                    </div>
                    <div className="space-y-2">
                      <Label>Devise</Label>
                      <Select value={formData.currency} onValueChange={(v) => setFormData({ ...formData, currency: v })}>
                        <SelectTrigger data-testid="expense-currency-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="EUR">EUR</SelectItem>
                          <SelectItem value="VND">VND</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Input data-testid="expense-description-input" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Détails de la dépense" />
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
                    <Button data-testid="submit-expense-button" type="submit" className="bg-slate-900 hover:bg-slate-800">Ajouter</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-600 uppercase tracking-wider">Total mensuel EUR</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold">{totalEur.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} &euro;</div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-600 uppercase tracking-wider">Total mensuel VND</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold">{totalVnd.toLocaleString('vi-VN', { maximumFractionDigits: 0 })} VND</div>
            </CardContent>
          </Card>
        </div>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <div className="flex items-center gap-2">
              <CreditCard size={20} className="text-slate-600" />
              <CardTitle className="text-lg font-medium">Dépenses mensuelles fixes</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div>
              </div>
            ) : expenses.length === 0 ? (
              <p className="text-center py-8 text-sm text-slate-500">Aucune dépense</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-medium">Type</TableHead>
                    <TableHead className="font-medium">Description</TableHead>
                    <TableHead className="font-medium text-right">Montant</TableHead>
                    <TableHead className="font-medium">Devise</TableHead>
                    <TableHead className="font-medium">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {expenses.map((e) => (
                    <TableRow key={e.id} data-testid="expense-row" className="hover:bg-slate-50">
                      <TableCell>
                        <Badge className="bg-slate-100 text-slate-800 border border-slate-200">{e.type}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">{e.description || '-'}</TableCell>
                      <TableCell className="text-right font-medium">{e.amount.toLocaleString('fr-FR')}</TableCell>
                      <TableCell>{e.currency}</TableCell>
                      <TableCell>
                        <Button data-testid="delete-expense-button" size="sm" variant="ghost" onClick={() => handleDeleteExpense(e.id)} className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50">
                          <Trash2 size={16} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Manage Types Dialog */}
        <Dialog open={typeDialogOpen} onOpenChange={setTypeDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Gérer les types de dépense</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex gap-2">
                <Input
                  data-testid="new-type-input"
                  value={newTypeName}
                  onChange={(e) => setNewTypeName(e.target.value)}
                  placeholder="Nouveau type..."
                  onKeyDown={(e) => e.key === 'Enter' && handleAddType()}
                />
                <Button data-testid="add-type-button" onClick={handleAddType} className="bg-slate-900 hover:bg-slate-800">Ajouter</Button>
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {expenseTypes.map(t => (
                  <div key={t.id} className="flex items-center justify-between p-2 border border-slate-200 rounded-md">
                    <span className="text-sm">{t.name}</span>
                    <Button size="sm" variant="ghost" onClick={() => handleDeleteType(t.id)} className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50">
                      <Trash2 size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
